/**
 * Snap system — module-level callback bridge + last-result slot.
 *
 * The renderer registers a raycast implementation via `registerSnapRaycast`.
 * The transform pipeline queries it via `querySnap` during live drag.
 * The snap indicator overlay reads the last result via `getLastSnapResult`.
 *
 * Follows the same pattern as `registerEditRaycast` / `raycastEdit` in
 * storeEditOps.ts.
 */

import * as THREE from "three/webgpu";
import type {
  AppState,
  AxisConstraint,
  SnapTarget,
} from "../app/storeTypes.ts";

// ---------------------------------------------------------------------------
// Snap result
// ---------------------------------------------------------------------------

export interface SnapResult {
  /** World-space position of the snap target. */
  worldPosition: [number, number, number];
  /** Object-local position of the snap target (for edit-mode transforms). */
  localPosition: [number, number, number];
  /** World-space target normal, when the snap target has a stable orientation. */
  worldNormal?: [number, number, number];
  /** What geometry element was matched. */
  hitType: SnapTarget;
}

// ---------------------------------------------------------------------------
// Callback bridge
// ---------------------------------------------------------------------------

type SnapRaycastFn = (
  ndcX: number,
  ndcY: number,
  target: SnapTarget,
  excludeObjectIds: Set<string>,
  snapGridLevelOffset?: number,
) => SnapResult | null;

let _snapRaycast: SnapRaycastFn | null = null;

/** Called by the renderer to provide (or remove) the snap raycast implementation. */
export function registerSnapRaycast(fn: SnapRaycastFn | null): void {
  _snapRaycast = fn;
}

/** Query the snap system for a target at the given mouse position (NDC). */
export function querySnap(
  ndcX: number,
  ndcY: number,
  target: SnapTarget,
  excludeObjectIds: Set<string>,
  snapGridLevelOffset = 0,
): SnapResult | null {
  return (
    _snapRaycast?.(ndcX, ndcY, target, excludeObjectIds, snapGridLevelOffset) ??
    null
  );
}

// ---------------------------------------------------------------------------
// Last snap result — shared between transform pipeline and indicator overlay
// ---------------------------------------------------------------------------

let _lastSnapResult: SnapResult | null = null;

export function setLastSnapResult(r: SnapResult | null): void {
  _lastSnapResult = r;
}

export function getLastSnapResult(): SnapResult | null {
  return _lastSnapResult;
}

// ---------------------------------------------------------------------------
// Dynamic Grid Spacing & Snapping Helper
// ---------------------------------------------------------------------------

/**
 * Calculate dynamic grid spacing based on camera distance.
 *
 * `snapGridLevelOffset` shifts by whole zoom levels: `0` keeps the default
 * step for the current zoom, `-1` moves one level finer (e.g. 1 -> 0.1).
 */
export function getDynamicSnapSpacing(
  gridLogDist: number,
  snapGridLevelOffset = 0,
): number {
  // Range: [0.01, 0.1, 1.0, 10.0]. Default is 1.0 (logDist ~0.5).
  // We apply a 0.5 bias so granular snapping activates "farther back".
  const exponent = Math.floor(gridLogDist - 0.5) + snapGridLevelOffset;
  return 10 ** Math.max(-2, Math.min(1, exponent));
}

// Reusable scratch objects
const _scratchVec3A = new THREE.Vector3();
const _scratchVec3B = new THREE.Vector3();
const _scratchVec3C = new THREE.Vector3();

/**
 * Shared snapping logic for applyCommandLive and Pose mode.
 *
 * Handles both geometry snapping (via querySnap) and grid snapping,
 * correctly projecting the result onto the active axis constraint.
 *
 * @param moveDir Input move direction to be modified (snapped) in-place.
 */
export function applySnapping(
  state: AppState,
  screenX: number,
  screenY: number,
  rect: DOMRect,
  sourcePos: THREE.Vector3 | [number, number, number],
  moveDir: THREE.Vector3,
  axis: AxisConstraint,
  normalVector: [number, number, number] | null,
  excludeObjectIds: Set<string> = new Set(),
  snapGridLevelOffset = 0,
): SnapResult | null {
  if (!state.snapEnabled) {
    setLastSnapResult(null);
    return null;
  }

  const ndcX = ((screenX - rect.left) / rect.width) * 2 - 1;
  const ndcY = -((screenY - rect.top) / rect.height) * 2 + 1;
  const snapResult = querySnap(
    ndcX,
    ndcY,
    state.snapTarget,
    excludeObjectIds,
    snapGridLevelOffset,
  );
  setLastSnapResult(snapResult);

  const spacing = getDynamicSnapSpacing(state.gridLogDist, snapGridLevelOffset);
  const src = _scratchVec3A.set(
    Array.isArray(sourcePos) ? sourcePos[0] : sourcePos.x,
    Array.isArray(sourcePos) ? sourcePos[1] : sourcePos.y,
    Array.isArray(sourcePos) ? sourcePos[2] : sourcePos.z,
  );

  if (snapResult) {
    // 1. Geometry Snap: Redirect moveDir to reach the hit point
    const target = _scratchVec3B.set(
      snapResult.worldPosition[0],
      snapResult.worldPosition[1],
      snapResult.worldPosition[2],
    );

    // Apply constraint projection
    if (axis === "normal" && normalVector) {
      const nv = _scratchVec3C.set(
        normalVector[0],
        normalVector[1],
        normalVector[2],
      );
      const len2 = nv.lengthSq();
      if (len2 > 1e-12) {
        const dot = target.sub(src).dot(nv);
        moveDir.copy(nv).multiplyScalar(dot / len2);
      }
    } else if (axis && axis !== "normal") {
      const av = _scratchVec3C.set(
        axis === "x" ? 1 : 0,
        axis === "y" ? 1 : 0,
        axis === "z" ? 1 : 0,
      );
      const dot = target.sub(src).dot(av);
      moveDir.copy(av).multiplyScalar(dot);
    } else {
      moveDir.set(target.x - src.x, target.y - src.y, target.z - src.z);
    }
  } else if (state.snapTarget === "grid") {
    // 2. Pure Grid Snap: Apply rounding to current moveDir along active axes
    if (axis === "normal" && normalVector) {
      const nv = _scratchVec3C.set(
        normalVector[0],
        normalVector[1],
        normalVector[2],
      );
      const len2 = nv.lengthSq();
      if (len2 > 1e-12) {
        // Find world position would-be and snap that, then derive delta
        const worldWouldBe = _scratchVec3B.copy(src).add(moveDir);
        const dot = worldWouldBe.dot(nv) / len2;
        const snappedDot = Math.round(dot / spacing) * spacing;
        const snappedWorld = _scratchVec3B.copy(nv).multiplyScalar(snappedDot);
        moveDir.copy(snappedWorld).sub(src);

        // Re-project back onto normal to ensure it's pure
        const finalDot = moveDir.dot(nv) / len2;
        moveDir.copy(nv).multiplyScalar(finalDot);
      }
    } else if (axis && axis !== "normal") {
      const av = _scratchVec3C.set(
        axis === "x" ? 1 : 0,
        axis === "y" ? 1 : 0,
        axis === "z" ? 1 : 0,
      );
      const worldWouldBe = _scratchVec3B.copy(src).add(moveDir);
      const dot = worldWouldBe.dot(av);
      const snappedDot = Math.round(dot / spacing) * spacing;
      const snappedWorld = _scratchVec3B.copy(av).multiplyScalar(snappedDot);
      moveDir.copy(snappedWorld).sub(src);

      // Re-project
      const finalDot = moveDir.dot(av);
      moveDir.copy(av).multiplyScalar(finalDot);
    } else {
      // Free drag grid snap: snap the absolute world position
      moveDir.x = Math.round((src.x + moveDir.x) / spacing) * spacing - src.x;
      moveDir.y = Math.round((src.y + moveDir.y) / spacing) * spacing - src.y;
      moveDir.z = Math.round((src.z + moveDir.z) / spacing) * spacing - src.z;
    }
  }

  return snapResult;
}

// ---------------------------------------------------------------------------
// Plane projection
// ---------------------------------------------------------------------------

/**
 * Project a world point onto the plane defined by `(planePoint, planeNormal)`.
 *
 * Used by the Surface Box tool to keep snapped corners on the locked face
 * plane: the snap system may return a vertex / edge / face hit that lives
 * off the plane, but the rectangle must stay co-planar with the host face.
 *
 * `planeNormal` must be unit length. Pass `out` to avoid per-frame
 * allocations when called from a pointermove rAF loop.
 */
export function projectPointOntoPlane(
  point: THREE.Vector3 | [number, number, number],
  planePoint: [number, number, number],
  planeNormal: [number, number, number],
  out?: THREE.Vector3,
): THREE.Vector3 {
  const px = Array.isArray(point) ? point[0] : point.x;
  const py = Array.isArray(point) ? point[1] : point.y;
  const pz = Array.isArray(point) ? point[2] : point.z;
  const dx = px - planePoint[0];
  const dy = py - planePoint[1];
  const dz = pz - planePoint[2];
  const d = dx * planeNormal[0] + dy * planeNormal[1] + dz * planeNormal[2];
  const target = out ?? new THREE.Vector3();
  target.set(
    px - d * planeNormal[0],
    py - d * planeNormal[1],
    pz - d * planeNormal[2],
  );
  return target;
}
